<template>
  <div class="main-body quote-page">
    <div class="title-bar no-print">
      <h1>{{ quoteTitle }}</h1>
    </div>

    <div class="quote-container">
      <div v-if="loading" class="loading"><img src="../assets/spinner.svg" /></div>
      <div v-else-if="quote" class="quote-content">
        <QuoteGeneralInformation
          :quote="quote"
          :selectedCompany="selectedCompany"
          :selectedIndividual="selectedIndividual"
          :contactOptions="contactOptions"
          :salespersonOptions="salespersonOptions"
          :showValidationErrors="showValidationErrors"
          @customerChanged="handleCustomerChanged" />
        <QuoteShippingInformation :quote="quote" :showValidationErrors="showValidationErrors" />
        <div class="divider"></div>
        <QuoteLineItems :quote="quote" :selectedCompany="selectedCompany" :selectedIndividual="selectedIndividual" />
        <div class="divider"></div>
        <QuoteSummary :quote="quote" />
      </div>
      <div v-else class="no-results">Quote not found</div>

      <div v-if="quote" class="quote-actions no-print">
        <div class="fill"></div>
        <button v-if="isNewQuote || user.quotesAdmin" class="text-btn red" @click="showDeleteConfirm = true">Delete</button>
        <button v-if="!isNewQuote" class="text-btn blue" @click="duplicateCurrentQuote">Duplicate</button>
        <button v-if="!isNewQuote" class="text-btn blue" @click="convertQuote" :disabled="isSaving || loading">Convert</button>
        <button class="text-btn blue" @click="printCurrentQuote" :disabled="isSaving || loading">Print</button>
        <button class="text-btn blue" @click="saveQuote(true)" :disabled="isSaving">Save & Close</button>
        <button class="square-btn" :class="{ saving: isSaving }" @click="saveQuote(false)" :disabled="isSaving">Save <img src="../assets/spinner.svg" /></button>
      </div>
    </div>
  </div>

  <QuotePrintDocument v-if="quote" ref="quotePrintDocument" :quote="quote" :selectedCompany="selectedCompany" :selectedIndividual="selectedIndividual" />

  <Confirmation
    v-if="showDeleteConfirm"
    @confirm="confirmDeleteOrCancel"
    @cancel="showDeleteConfirm = false"
    :header="deleteConfirmHeader"
    :message="deleteConfirmMessage"
    :confirmText="deleteConfirmText"
    cancelText="Cancel"
    :serious="true" />

  <textarea ref="copyLineItemsElement" class="copy-line-items-textarea"></textarea>
</template>

<script>
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import QuoteGeneralInformation from '@/components/quotes/QuoteGeneralInformation.vue';
import QuoteShippingInformation from '@/components/quotes/QuoteShippingInformation.vue';
import QuoteLineItems from '@/components/quotes/QuoteLineItems.vue';
import QuoteSummary from '@/components/quotes/QuoteSummary.vue';
import QuotePrintDocument from '@/components/quotes/QuotePrintDocument.vue';
import Confirmation from '@/components/Confirmation.vue';
import useQuoteQuery from '@/composables/useQuoteQuery';
import useQuoteMutate from '@/composables/useQuoteMutate';
import useCompaniesQuery from '@/composables/useCompaniesQuery';
import useCompanyQuery from '@/composables/useCompanyQuery';
import useContactQuery from '@/composables/useContactQuery';
import useUsersQuery from '@/composables/useUsersQuery';
import useCurrentUser from '@/composables/useCurrentUser';
import useSaveProtect from '@/composables/useSaveProtect';
import useAlerts from '@/composables/useAlerts';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useQuoteProductsQuery from '@/composables/useQuoteProductsQuery';
import { createBlankLine, ensureTrailingBlankLine, normalizeLineItemsForSave, buildLineItemsClipboardText } from '@/composables/useQuoteLineItems';

export default {
  name: 'Quote',
  components: {
    QuoteGeneralInformation,
    QuoteShippingInformation,
    QuoteLineItems,
    QuoteSummary,
    QuotePrintDocument,
    Confirmation,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const getQuote = useQuoteQuery();
    const queryCompany = useCompanyQuery();
    const queryContact = useContactQuery();
    const { addQuote, updateQuote, deleteQuote, duplicateQuote } = useQuoteMutate();
    const { getUsers, salespeople, outsideSalespeople, allActiveUsers } = useUsersQuery();
    const { user } = useCurrentUser();
    const { state: saveProtectState } = useSaveProtect();
    const { addAlert } = useAlerts();
    const { decryptSymbols, encryptSymbols } = useDecryptSymbols();

    useCompaniesQuery.useCompaniesQuery();
    useQuoteProductsQuery.useQuoteProductsQuery();
    getUsers();

    const quote = ref(null);
    const originalQuote = ref(null);
    const quoteQuery = ref(null);
    const companyQuery = ref(null);
    const contactQuery = ref(null);
    const selectedCompany = ref(null);
    const selectedIndividual = ref(null);
    const companyContacts = ref([]);
    const loading = ref(false);
    const isSaving = ref(false);
    const quotePrintDocument = ref(null);
    const copyLineItemsElement = ref(null);
    const showValidationErrors = ref(false);
    const showDeleteConfirm = ref(false);
    const hasPrinted = ref(false);

    const isNewQuote = computed(() => route.params.quoteid === 'new');

    const quoteTitle = computed(() => {
      if (isNewQuote.value) return 'New Quote';
      return 'Edit Quote';
    });

    const deleteConfirmHeader = computed(() => (isNewQuote.value ? 'Cancel Quote' : 'Delete Quote'));
    const deleteConfirmMessage = computed(() =>
      isNewQuote.value ? 'Are you sure you want to cancel this quote?' : 'Are you sure you want to delete this quote?'
    );
    const deleteConfirmText = computed(() => (isNewQuote.value ? 'Yes' : 'Delete'));

    const contactOptions = computed(() => {
      // Inactive contacts are only offered when the quote already uses them
      return companyContacts.value
        .filter((contact) => !contact.INACTIVE || contact.CONTACTID === quote.value?.CONTACTID || contact.CONTACTID === originalQuote.value?.CONTACTID)
        .map((contact) => ({
          text: `${contact.FIRSTNAME || ''} ${contact.LASTNAME || ''}`.trim() + (contact.INACTIVE ? ' (Inactive)' : ''),
          value: contact.CONTACTID,
        }));
    });

    const salespersonOptions = computed(() => {
      const seen = new Set();
      const combined = [...salespeople, ...outsideSalespeople, ...allActiveUsers].filter((salesperson) => {
        if (seen.has(salesperson.USERID)) return false;
        seen.add(salesperson.USERID);
        return salesperson.INSIDESALES || salesperson.OUTSIDESALES;
      });
      combined.sort((a, b) => a.DISPLAYNAME.localeCompare(b.DISPLAYNAME));
      return combined.map((salesperson) => ({ text: salesperson.DISPLAYNAME, value: salesperson.USERID }));
    });

    function formatDateForInput(dateString) {
      if (!dateString) return '';
      const timestamp = parseInt(dateString);
      const date = !isNaN(timestamp) ? new Date(timestamp) : new Date(dateString);
      if (date.toString() === 'Invalid Date') return '';
      return date.toISOString().split('T')[0];
    }

    function getDefaultValidTo() {
      const date = new Date();
      date.setMonth(date.getMonth() + 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    function queryParam(name) {
      const value = route.query[name];
      if (Array.isArray(value)) return value[0] || null;
      return value || null;
    }

    function makeNewQuote() {
      return {
        COMPANYID: queryParam('companyid'),
        CONTACTID: queryParam('contactid'),
        ASSIGNEDID: user.userid || null,
        JOBNAME: '',
        DATEREQUIRED: '',
        VALIDTO: getDefaultValidTo(),
        CREATEDATE: new Date().toISOString(),
        STATUS: 'open',
        NOTES: '',
        SHIPVIA: '',
        SHIPLINE1: '',
        SHIPLINE2: '',
        SHIPCITY: '',
        SHIPSTATE: '',
        SHIPPOSTALCODE: '',
        SHIPEMAIL: '',
        SHIPPHONE: '',
        DISCOUNTPCT: 0,
        items: [createBlankLine()],
      };
    }

    function prepareQuote(incomingQuote) {
      const preparedQuote = JSON.parse(JSON.stringify(incomingQuote));
      preparedQuote.STATUS = preparedQuote.STATUS || 'open';
      preparedQuote.JOBNAME = preparedQuote.JOBNAME || '';
      preparedQuote.NOTES = preparedQuote.NOTES || '';
      preparedQuote.SHIPVIA = preparedQuote.SHIPVIA || '';
      preparedQuote.SHIPLINE1 = preparedQuote.SHIPLINE1 || '';
      preparedQuote.SHIPLINE2 = preparedQuote.SHIPLINE2 || '';
      preparedQuote.SHIPCITY = preparedQuote.SHIPCITY || '';
      preparedQuote.SHIPSTATE = preparedQuote.SHIPSTATE || '';
      preparedQuote.SHIPPOSTALCODE = preparedQuote.SHIPPOSTALCODE || '';
      preparedQuote.SHIPEMAIL = preparedQuote.SHIPEMAIL || '';
      preparedQuote.SHIPPHONE = formatPhoneNumber(preparedQuote.SHIPPHONE || '');
      preparedQuote.DISCOUNTPCT = preparedQuote.DISCOUNTPCT === '' || preparedQuote.DISCOUNTPCT === null || preparedQuote.DISCOUNTPCT === undefined ? 0 : Number(preparedQuote.DISCOUNTPCT);
      preparedQuote.DATEREQUIRED = formatDateForInput(preparedQuote.DATEREQUIRED);
      preparedQuote.VALIDTO = formatDateForInput(preparedQuote.VALIDTO);
      preparedQuote.items = (preparedQuote.items || []).sort((a, b) => (a.SORTORDER || 0) - (b.SORTORDER || 0));
      ensureTrailingBlankLine(preparedQuote.items);
      return preparedQuote;
    }

    function formatPhoneNumber(value) {
      const cleaned = String(value).replace(/\D/g, '').slice(0, 13);
      if (!cleaned.length) return '';
      if (cleaned.length <= 3) return cleaned;
      if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      if (cleaned.length <= 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      const countryCodeLength = cleaned.length - 10;
      const countryCode = cleaned.slice(0, countryCodeLength);
      const areaCode = cleaned.slice(countryCodeLength, countryCodeLength + 3);
      const firstPart = cleaned.slice(countryCodeLength + 3, countryCodeLength + 6);
      const secondPart = cleaned.slice(countryCodeLength + 6);
      return `+${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
    }

    function executeQuery() {
      showValidationErrors.value = false;
      hasPrinted.value = false;
      saveProtectState.isDirty = false;

      if (isNewQuote.value) {
        quoteQuery.value = null;
        quote.value = makeNewQuote();
        originalQuote.value = JSON.parse(JSON.stringify(quote.value));
        loadSelectedCustomer();
        return;
      }

      quote.value = null;
      originalQuote.value = null;
      loading.value = true;
      quoteQuery.value = getQuote(route.params.quoteid);
    }

    function loadSelectedCustomer() {
      companyContacts.value = [];
      selectedCompany.value = null;
      selectedIndividual.value = null;
      companyQuery.value = null;
      contactQuery.value = null;

      if (quote.value?.COMPANYID) {
        companyQuery.value = queryCompany(quote.value.COMPANYID);
        return;
      }

      if (quote.value?.CONTACTID) {
        contactQuery.value = queryContact(quote.value.CONTACTID);
      }
    }

    function handleCustomerChanged() {
      loadSelectedCustomer();
    }

    function removeTypenames(obj) {
      if (Array.isArray(obj)) return obj.map(removeTypenames);
      if (obj !== null && typeof obj === 'object') {
        const newObj = {};
        for (const key in obj) {
          if (key !== '__typename') newObj[key] = removeTypenames(obj[key]);
        }
        return newObj;
      }
      return obj;
    }

    function cleanQuoteForSave() {
      const quoteToSave = removeTypenames(JSON.parse(JSON.stringify(quote.value)));
      delete quoteToSave.QUOTEID;
      delete quoteToSave.QUOTENUM;
      delete quoteToSave.company;
      delete quoteToSave.contact;
      delete quoteToSave.salesperson;

      // Create date is set on the client for new quotes only; never update it after save
      if (!isNewQuote.value) {
        delete quoteToSave.CREATEDATE;
      }

      if (!quoteToSave.DATEREQUIRED) {
        quoteToSave.DATEREQUIRED = null;
      }

      if (!quoteToSave.VALIDTO) {
        quoteToSave.VALIDTO = null;
      }

      if (!quoteToSave.SHIPVIA) {
        quoteToSave.SHIPVIA = null;
      }

      if (!quoteToSave.SHIPLINE1) {
        quoteToSave.SHIPLINE1 = null;
      }

      if (!quoteToSave.SHIPLINE2) {
        quoteToSave.SHIPLINE2 = null;
      }

      if (!quoteToSave.SHIPCITY) {
        quoteToSave.SHIPCITY = null;
      }

      if (!quoteToSave.SHIPSTATE) {
        quoteToSave.SHIPSTATE = null;
      }

      if (!quoteToSave.SHIPPOSTALCODE) {
        quoteToSave.SHIPPOSTALCODE = null;
      }

      if (!quoteToSave.SHIPEMAIL) {
        quoteToSave.SHIPEMAIL = null;
      }

      if (!quoteToSave.SHIPPHONE) {
        quoteToSave.SHIPPHONE = null;
      }

      quoteToSave.DISCOUNTPCT = quoteToSave.DISCOUNTPCT === '' || quoteToSave.DISCOUNTPCT === null || quoteToSave.DISCOUNTPCT === undefined ? 0 : Number(quoteToSave.DISCOUNTPCT);

      quoteToSave.items = normalizeLineItemsForSave(quoteToSave.items);

      return encryptSymbols(quoteToSave);
    }

    function validateQuote() {
      if (!quote.value.COMPANYID && !quote.value.CONTACTID) {
        addAlert('Company or individual is required', 'high', 3);
        return false;
      }
      if (!quote.value.VALIDTO) {
        addAlert('Valid to is required', 'high', 3);
        return false;
      }
      if (!quote.value.JOBNAME?.trim()) {
        addAlert('Job name is required', 'high', 3);
        return false;
      }
      if (!quote.value.ASSIGNEDID) {
        addAlert('Salesperson is required', 'high', 3);
        return false;
      }
      if (!quote.value.DATEREQUIRED) {
        addAlert('Date required is required', 'high', 3);
        return false;
      }
      if (!quote.value.SHIPPHONE?.trim()) {
        addAlert('Shipping phone is required', 'high', 3);
        return false;
      }
      const lineItems = normalizeLineItemsForSave(quote.value.items);
      const incompletePartLine = lineItems.find((item) => item.LINETYPE === 'part' && !item.PRODUCTID);
      if (incompletePartLine) {
        addAlert('Part line items require an item number', 'high', 3);
        return false;
      }
      const incompleteNonStockLine = lineItems.find((item) => item.LINETYPE === 'nonstock' && !item.DESCRIPTION);
      if (incompleteNonStockLine) {
        addAlert('Non-stock line items require a description', 'high', 3);
        return false;
      }
      const incompleteCommentLine = lineItems.find((item) => item.LINETYPE === 'comment' && !item.DESCRIPTION);
      if (incompleteCommentLine) {
        addAlert('Comment line items require a comment', 'high', 3);
        return false;
      }
      return true;
    }

    async function saveQuote(closeAfterSave = false, options = {}) {
      if (!validateQuote()) {
        showValidationErrors.value = true;
        return false;
      }

      isSaving.value = true;
      const quoteToSave = cleanQuoteForSave();

      try {
        if (isNewQuote.value) {
          const response = await addQuote(quoteToSave);
          if (!response?.addQuote) return false;
          saveProtectState.isDirty = false;
          addAlert(options.successAlert || 'Quote saved successfully', 'low', 2);
          if (closeAfterSave) {
            await router.push({ name: 'quotes' });
          } else {
            const nextRoute = { name: 'quote', params: { quoteid: response.addQuote } };
            if (options.printAfterSave) nextRoute.query = { print: 'true' };
            await router.replace(nextRoute);
          }
        } else {
          const response = await updateQuote(route.params.quoteid, quoteToSave);
          if (!response?.updateQuote) return false;
          originalQuote.value = JSON.parse(JSON.stringify(quote.value));
          saveProtectState.isDirty = false;
          addAlert(options.successAlert || 'Quote saved successfully', 'low', 2);
          if (closeAfterSave) await router.push({ name: 'quotes' });
        }

        return true;
      } finally {
        isSaving.value = false;
      }
    }

    async function confirmDeleteOrCancel() {
      showDeleteConfirm.value = false;

      if (isNewQuote.value) {
        saveProtectState.isDirty = false;
        router.push({ name: 'quotes' });
        return;
      }

      const response = await deleteQuote(route.params.quoteid);
      if (response?.deleteQuote) {
        saveProtectState.isDirty = false;
        addAlert('Quote deleted', 'low', 2);
        router.push({ name: 'quotes' });
      }
    }

    async function duplicateCurrentQuote() {
      const response = await duplicateQuote(route.params.quoteid);
      if (response?.duplicateQuote) {
        saveProtectState.isDirty = false;
        addAlert('Quote duplicated', 'low', 2);
        router.push({ name: 'quote', params: { quoteid: response.duplicateQuote } });
      }
    }

    function copyLineItemsToClipboard() {
      const text = buildLineItemsClipboardText(quote.value?.items || [], useQuoteProductsQuery.quoteProducts);
      if (!text) return false;

      const textarea = copyLineItemsElement.value;
      if (!textarea) return false;

      textarea.value = text;
      textarea.select();
      document.execCommand('copy');
      return true;
    }

    async function convertQuote() {
      if (!quote.value || isNewQuote.value || isSaving.value || loading.value) return;

      if (!copyLineItemsToClipboard()) {
        addAlert('No part line items to copy', 'high', 3);
        return;
      }

      const previousStatus = quote.value.STATUS;
      quote.value.STATUS = 'converted';

      const saved = await saveQuote(false, { successAlert: 'Converted' });
      if (!saved) {
        quote.value.STATUS = previousStatus;
      }
    }

    function wait(milliseconds) {
      return new Promise((resolve) => setTimeout(resolve, milliseconds));
    }

    function waitForAnimationFrames(frameCount = 2) {
      return new Promise((resolve) => {
        const step = (remaining) => {
          if (remaining <= 0) {
            resolve();
            return;
          }
          requestAnimationFrame(() => step(remaining - 1));
        };
        step(frameCount);
      });
    }

    async function waitForCustomerReady() {
      if (!quote.value?.COMPANYID && !quote.value?.CONTACTID) return;

      const isReady = () =>
        quote.value?.COMPANYID
          ? !!selectedCompany.value || !!quote.value?.company?.address
          : !!selectedIndividual.value || !!quote.value?.contact?.address;
      if (isReady()) return;

      for (let attempt = 0; attempt < 20; attempt += 1) {
        await wait(50);
        if (isReady()) return;
      }
    }

    async function waitForPrintDocument() {
      for (let attempt = 0; attempt < 40; attempt += 1) {
        if (quotePrintDocument.value?.prepareForPrint) return quotePrintDocument.value;
        await nextTick();
        await wait(25);
      }
      return null;
    }

    async function preparePrintDocument() {
      if (!quote.value || loading.value) return false;

      await waitForCustomerReady();
      const printDocument = await waitForPrintDocument();
      if (!printDocument) return false;

      await printDocument.prepareForPrint();
      await nextTick();
      await waitForAnimationFrames(2);

      return Array.isArray(printDocument.pages) && printDocument.pages.length > 0;
    }

    function loadedQuoteMatchesRoute() {
      const loadedQuoteId = quote.value?.QUOTEID;
      const routeQuoteId = route.params.quoteid;
      return loadedQuoteId && routeQuoteId && String(loadedQuoteId).toUpperCase() === String(routeQuoteId).toUpperCase();
    }

    async function consumePrintQuery() {
      const query = { ...route.query };
      delete query.print;
      await router.replace({ name: 'quote', params: { quoteid: route.params.quoteid }, query }).catch(() => {});
    }

    const QUOTE_PRINT_STYLE_ID = 'quote-print-page-orientation';

    function applyQuotePrintOrientation() {
      let style = document.getElementById(QUOTE_PRINT_STYLE_ID);
      if (!style) {
        style = document.createElement('style');
        style.id = QUOTE_PRINT_STYLE_ID;
        document.head.appendChild(style);
      }
      style.textContent = `
        @media print {
          @page {
            size: 8.5in 11in;
            margin: 0;
          }
        }
      `;
      document.documentElement.classList.add('quote-print-active');
    }

    function removeQuotePrintOrientation() {
      document.getElementById(QUOTE_PRINT_STYLE_ID)?.remove();
      document.documentElement.classList.remove('quote-print-active');
    }

    async function printPreparedQuote(options = {}) {
      const ready = await preparePrintDocument();
      if (!ready) return false;

      await nextTick();
      await waitForAnimationFrames(2);

      applyQuotePrintOrientation();
      const cleanupOrientation = () => removeQuotePrintOrientation();
      window.addEventListener('afterprint', cleanupOrientation, { once: true });
      window.print();

      if (options.consumePrintQuery) await consumePrintQuery();
      return true;
    }

    async function printCurrentQuote() {
      if (!quote.value || isSaving.value || loading.value) return;
      const printAfterSave = isNewQuote.value;
      const saved = await saveQuote(false, { printAfterSave });
      if (!saved || printAfterSave) return;
      await printPreparedQuote();
    }

    async function maybePrint() {
      if (route.query.print !== 'true' || !quote.value || hasPrinted.value || isNewQuote.value || loading.value || !loadedQuoteMatchesRoute()) {
        return;
      }

      hasPrinted.value = true;
      const printed = await printPreparedQuote({ consumePrintQuery: true });
      if (!printed) hasPrinted.value = false;
    }

    watch(
      () => [route.params.quoteid, route.query.companyid, route.query.contactid],
      executeQuery,
      { immediate: true }
    );

    watch(
      // quote/loading are auto-unwrapped when the query object is stored in a ref
      () => quoteQuery.value?.quote,
      (newQuote) => {
        if (!newQuote) return;
        quote.value = prepareQuote(newQuote);
        originalQuote.value = JSON.parse(JSON.stringify(quote.value));
        loading.value = false;
        loadSelectedCustomer();
        nextTick(() => {
          maybePrint();
          // Re-baseline after mount normalizations (e.g. phone formatting)
          if (!quote.value) return;
          originalQuote.value = JSON.parse(JSON.stringify(quote.value));
          saveProtectState.isDirty = false;
        });
      },
      { deep: true }
    );

    watch(
      () => quoteQuery.value?.loading,
      (newLoading) => {
        if (newLoading !== undefined) loading.value = newLoading;
        if (newLoading === false) maybePrint();
      }
    );

    watch(
      // result is auto-unwrapped when the query object is stored in a ref
      () => companyQuery.value?.result?.company,
      (company) => {
        const decryptedCompany = decryptSymbols(company || {});
        selectedCompany.value = company ? decryptedCompany : null;
        companyContacts.value = decryptedCompany.contacts || [];
        // Don't mutate quote.company on load — that falsely marks the form dirty.
        if (quote.value?.CONTACTID && companyContacts.value.length && !companyContacts.value.some((contact) => contact.CONTACTID === quote.value.CONTACTID)) {
          quote.value.CONTACTID = null;
        }
      },
      { deep: true }
    );

    watch(
      () => contactQuery.value?.result?.contact,
      (contact) => {
        selectedIndividual.value = contact ? decryptSymbols(contact) : null;
      },
      { deep: true }
    );

    watch(
      () => user.userid,
      () => {
        if (isNewQuote.value && quote.value && !quote.value.ASSIGNEDID) {
          quote.value.ASSIGNEDID = user.userid;
          originalQuote.value = JSON.parse(JSON.stringify(quote.value));
        }
      }
    );

    watch(
      quote,
      () => {
        if (!quote.value || !originalQuote.value) {
          saveProtectState.isDirty = false;
          return;
        }
        saveProtectState.isDirty = JSON.stringify(quote.value) !== JSON.stringify(originalQuote.value);
      },
      { deep: true }
    );

    watch(() => route.query.print, maybePrint);

    return {
      quote,
      quotePrintDocument,
      copyLineItemsElement,
      loading,
      isSaving,
      showValidationErrors,
      showDeleteConfirm,
      user,
      isNewQuote,
      quoteTitle,
      deleteConfirmHeader,
      deleteConfirmMessage,
      deleteConfirmText,
      selectedCompany,
      selectedIndividual,
      contactOptions,
      salespersonOptions,
      handleCustomerChanged,
      saveQuote,
      confirmDeleteOrCancel,
      duplicateCurrentQuote,
      convertQuote,
      printCurrentQuote,
    };
  },
};
</script>

<style lang="scss" scoped>
.quote-page {
  .quote-container {
    background: white;
    flex: 1;
    overflow: auto;
    display: flex;
    flex-direction: column;
  }

  .loading {
    width: 100%;
    min-height: 20rem;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 2rem;
      height: 2rem;
    }
  }

  .quote-content {
    display: flex;
    flex-direction: column;
    gap: 3rem;
    padding: 3rem 3.75rem;
    flex: 1;
  }

  .divider {
    width: calc(100% + 7.5rem);
    margin: -1.5rem -3.75rem;
    height: 1.5rem;
    background: $appBackgroundColor;
    flex-shrink: 0;
  }

  .quote-actions {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: white;
    border-top: 1px solid $inputBorderColor;
    padding: 1rem 3.75rem;
    display: flex;
    align-items: center;
    gap: 1rem;

    .fill {
      flex: 1;
    }
  }

}

.copy-line-items-textarea {
  position: fixed;
  left: -9999px;
  top: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

@media print {
  .quote-page {
    display: none !important;
  }

  .no-print,
  .quote-actions,
  .divider {
    display: none !important;
  }

  .quote-page .quote-content {
    padding: 0;
    gap: 1rem;
  }
}
</style>
